import { cn } from '../../../../lib/utils';
import { HelpFill, InfoFill, ErrorFill } from '@crabs-ai/web-icons';
import Tooltip, { TooltipProps } from './Tooltip';

export type TooltipIconType = 'help' | 'info' | 'error';

export interface TooltipTriggerIconProps {
  /** 아이콘 타입 */
  iconType?: TooltipIconType;
  /** 툴팁 텍스트 */
  tooltipText: string;
  /** 아이콘 색상 클래스 (fill 관련) */
  iconColorClassName?: string;
  /** 아이콘 크기 클래스 */
  iconSizeClassName?: string;
  /** 추가 Tooltip Props */
  tooltipProps?: Partial<Omit<TooltipProps, 'textNode' | 'children'>>;
  /** button 내부처럼 중첩 button이 금지되는 위치에서 사용하는 비인터랙티브 trigger 여부 */
  isNonInteractiveTrigger?: boolean;
}

const iconComponents = {
  help: HelpFill,
  info: InfoFill,
  error: ErrorFill,
} as const;

/**
 * Tooltip Trigger로 사용되는 아이콘 컴포넌트
 * - 아이콘 타입(help, info, error) 변경 가능
 * - 부모 컴포넌트의 텍스트와 동일한 색상 적용 가능
 * - 16px 크기 기본값
 *
 * @example
 * // 헤더 제목 옆에서 사용
 * <TooltipTriggerIcon tooltipText="도움말 내용" />
 *
 * // 카드 제목 옆에서 사용
 * <TooltipTriggerIcon iconType="info" tooltipText="정보 내용" />
 *
 * // 커스텀 색상 적용
 * <TooltipTriggerIcon
 *   tooltipText="에러 내용"
 *   iconType="error"
 *   iconColorClassName="[&_path]:fill-sys-cont-negative-default"
 * />
 */
export default function TooltipTriggerIcon({
  iconType = 'help',
  tooltipText,
  iconColorClassName = '[&_path]:fill-sys-cont-neutral-light',
  iconSizeClassName = 'size-4',
  tooltipProps,
  isNonInteractiveTrigger = false,
}: TooltipTriggerIconProps) {
  const IconComponent = iconComponents[iconType];
  const mergedTooltipProps = isNonInteractiveTrigger
    ? {
        ...tooltipProps,
        triggerProps: {
          ...tooltipProps?.triggerProps,
          asChild: true,
        },
      }
    : tooltipProps;

  return (
    <Tooltip
      textNode={tooltipText}
      {...mergedTooltipProps}
    >
      <span
        className="flex items-center"
        onClick={(e) => e.stopPropagation()}
        {...(!isNonInteractiveTrigger && { role: 'button' })}
      >
        <IconComponent className={cn(iconSizeClassName, iconColorClassName)} />
      </span>
    </Tooltip>
  );
}
